import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Send, Paperclip, User, Mail, Tag, MessageCircle } from "lucide-react";

function Support() {
  const { currentUser, userProfile } = useAuth();
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setAttachment(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) {
      alert("Please fill out subject and message.");
      return;
    }

    setIsSubmitting(true);
    try {
      let attachmentUrl = null;
      if (attachment) {
        const storage = getStorage();
        const storageRef = ref(
          storage,
          `support_attachments/${currentUser?.uid || "anonymous"}/${Date.now()}_${attachment.name}`
        );
        const snapshot = await uploadBytes(storageRef, attachment);
        attachmentUrl = await getDownloadURL(snapshot.ref);
      }

      await addDoc(collection(db, "supportTickets"), {
        userId: currentUser?.uid || null,
        userEmail: userProfile?.email || currentUser?.email || "",
        userName: userProfile?.full_name || userProfile?.name || "",
        subject,
        category,
        message,
        attachmentUrl,
        status: "open",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });

      setSubject("");
      setCategory("general");
      setMessage("");
      setAttachment(null);
      alert("Your message was sent. We'll get back to you shortly.");
    } catch (err) {
      console.error("Error submitting support ticket:", err);
      alert("Failed to send your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
        <MessageCircle className="w-6 h-6 text-emerald-400 mr-2" />
        Support
      </h2>

      <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={userProfile?.full_name || userProfile?.name || ""}
                  disabled
                  className="block w-full pl-12 pr-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white placeholder-gray-400 focus:outline-none"
                  placeholder="Your name"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={userProfile?.email || currentUser?.email || ""}
                  disabled
                  className="block w-full pl-12 pr-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white placeholder-gray-400 focus:outline-none"
                  placeholder="you@example.com"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Tag className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="block w-full pl-12 pr-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="What's this about?"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="block w-full px-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="general">General</option>
                <option value="deposit">Deposits</option>
                <option value="withdrawal">Withdrawals</option>
                <option value="account">Account</option>
                <option value="bug">Bug Report</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
            <textarea
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Describe your issue or feedback..."
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
              <Paperclip className="w-4 h-4" />
              <span>Attach file (optional)</span>
              <input type="file" className="hidden" onChange={handleFileChange} />
              {attachment && (
                <span className="text-gray-400 text-sm truncate max-w-[200px]">{attachment.name}</span>
              )}
            </label>

            <button
              type="submit"
              disabled={isSubmitting || !subject || !message}
              className="bg-emerald-500 hover:bg-emerald-600 text-white py-3 px-5 rounded-lg transition-colors font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Support;


